// src/utils/transport.js
// Shared helpers for the Transport module. The transport registry (`transports`
// collection) stores a transport's name + GST. When a transport is assigned to a
// customer or quoted on a quotation, the name and GST are combined into a single
// display string (the legacy `transport_name` field) so the existing quotation +
// print / transport-slip code keeps working unchanged.

export const toTitleCase = (value) =>
  String(value || "").replace(/\b\w/g, char => char.toUpperCase());

// Marker id used by the "✍️ Custom transport…" option in TransportSelect. A custom
// transport is not yet backed by a `transports` doc — on quotation save it is
// registered into the transport module and this marker is replaced with the real id.
export const CUSTOM_TRANSPORT_ID = "__custom__";

// Build the single-field display string rendered on quotations / slips.
//   "VRL Logistics"                  → name only
//   "VRL Logistics (GST: 24AB...Z5)" → name + GST
export const getTransportDisplayName = (transport) => {
  const name = String(transport?.name || "").trim();
  const gst = String(transport?.gst || "").trim().toUpperCase();
  if (!name) return "";
  return gst ? `${name} (GST: ${gst})` : name;
};

// The plain transport name (GST suffix stripped) from a combined display string.
// Given "VRL (GST: 24AB...)" + "24AB..." → "VRL". When no GST is supplied the
// string is returned as-is, which preserves legacy free-text transport names.
export const getTransportName = (name = "", gst = "") => {
  const s = String(name || "").trim();
  const g = String(gst || "").trim().toUpperCase();
  if (g && s.includes(`(GST: ${g})`)) {
    return s.replace(`(GST: ${g})`, "").trim();
  }
  return s;
};

// Find an active transport by its Firestore id from a list.
export const findTransportById = (transports = [], id) =>
  transports.find(t => t && t.id === id && t.status !== "deleted") || null;

// The active transports a dropdown should offer, sorted by name.
export const getActiveTransports = (transports = []) =>
  [...transports]
    .filter(t => t && t.status !== "deleted")
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

// ─── Validation helpers (code level, not database level) ──────────────────────
// The transport registry enforces:
//   1. A unique (name + GST) combination.
//   2. Names stored in Title Case, GST numbers in UPPER CASE.
//   3. When the GST is empty, the name alone must be unique.

// Normalized transport name: trimmed, inner whitespace collapsed, Title Case
// (each word capitalized) — the naming convention used across the app.
export const normalizeTransportName = (name) =>
  toTitleCase(String(name || "").trim().replace(/\s+/g, " "));

// Normalized GST number: trimmed + UPPER CASE.
export const normalizeTransportGst = (gst) =>
  String(gst || "").trim().toUpperCase();

/**
 * Find the active transport that conflicts with the given name / GST pair.
 *
 *   - GST provided → conflicts only when another transport has BOTH the same
 *     name AND the same GST (the combination must be unique).
 *   - GST empty    → conflicts when another transport has the same name,
 *     regardless of that transport's GST (the name alone must be unique).
 *
 * Soft-deleted transports never conflict, and `excludeId` lets an edit skip
 * its own record. Name comparison is case-insensitive so legacy lowercase
 * records are still detected. Returns the conflicting transport or `null`.
 */
export const findTransportConflict = (transports = [], { name, gst } = {}, excludeId = null) => {
  const normName = normalizeTransportName(name).toLowerCase();
  const normGst = normalizeTransportGst(gst);
  if (!normName) return null;

  return transports.find(t => {
    if (!t || t.id === excludeId || t.status === "deleted") return false;
    if (normalizeTransportName(t.name).toLowerCase() !== normName) return false;
    // With a GST the pair (name + GST) must match; without one, name alone.
    return normGst ? normalizeTransportGst(t.gst) === normGst : true;
  }) || null;
};

// User-facing message for a conflict returned by findTransportConflict.
export const getTransportConflictMessage = (conflict, gst) => {
  const name = normalizeTransportName(conflict?.name);
  return gst
    ? `Transport with name "${name}" and GST ${normalizeTransportGst(gst)} already exists.`
    : `Transport name "${name}" already exists. Add a GST number or use a different name.`;
};
